import React, { useContext, useState, useCallback, useEffect, useMemo } from 'react';
import _ from 'underscore';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { ChoiceGroup, Checkbox, Dropdown } from '@fluentui/react';
import { Tooltip } from '@fluentui/react-components';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { AccordionHeader, getStepStyles } from '@bingads-webui-campaign-react/conversion-wizard-components';
import { GoalCreationWizardContext } from '../../goal-creation-wizard-context';
import { UncheckConversionDialog } from '../components/uncheck-include-conversion-dialog/uncheck-conversion-dialog';
import { ConversionWindow } from '../../components/index';
import { LearnMore } from '../../../../conversion-wizard-components/src/learn-more/learn-more';
import { duration2Input } from '../../shared/util';
import { NetworkCards } from './network-cards';
import { LinkedInCampaignAssociationSelector } from '../components/linkedin-campaign-selector';

const fullTimeTemplate = (obj, i18n) => `${obj.day} ${i18n.getString(_TL_('days,'))} ${obj.hour} ${i18n.getString(_TL_('hours, and'))} ${obj.min} ${i18n.getString(_TL_('minutes'))}`;
const dayTemplate = (obj, i18n) => `${obj.day} ${i18n.getString(_TL_('days'))}`;

const mappingChoiceText = (config, keyName) => {
  const index = _.indexOf(_.pluck(config, 'key'), keyName);
  return config[index].text;
};

const AdvancedGoalSettingsViewUnstyled = (props) => {
  const {
    classes,
    selectedGoalType,
    updateGoalContext,
    expandError,
    resetExpandError,
    isConversionGoalAttributionModelEnabled = false,
    setAdvSettingHasError,
    showLinkedInCards,
    goalHasAssociatedCampaigns,
    associatedCampaigns,
    onAssociatedCampaignsChange,
  } = props;
  const i18n = useContext(I18nContext);
  const wizardContext = useContext(GoalCreationWizardContext);
  const { logActivity, isEditScenario } = wizardContext;
  const { Title: accountName } = wizardContext.serverContext.CurrentAccount;
  const {
    IsAccountLevel,
    ConversionCountType,
    IsMainConversionGoal,
    LookbackWindowInMinutes,
    ViewThroughLookbackWindowinMinutes,
    AttributionModelType,
    SelectedNetwork,
  } = wizardContext.goalContext;

  const IsViewThroughConversionWindowVisible = useMemo(() => {
    const isViewthroughConversionEnabled = wizardContext.permissions && wizardContext.permissions.IsViewThroughConversionEnabled;
    const nonVTGoalTypes = ['ApplicationInstallGoal', 'OfflineConversionGoal', 'InStoreTransactionGoal', 'InStoreVisitGoal'];
    if (nonVTGoalTypes.indexOf(wizardContext.goalContext.Type) > -1) {
      return false;
    }
    return isViewthroughConversionEnabled;
  }, [wizardContext.goalContext.Type, wizardContext.permissions]);

  const IsAmplifyEnabled = useMemo(() =>
    wizardContext.permissions && wizardContext.permissions.IsAmplifyEnabled, [wizardContext.permissions]);

  const {
    CurrentAccount: { Id: aid },
    CurrentCustomer: { Id: cid },
  } = wizardContext.serverContext;

  const enableGoalLevelChange = useMemo(() => {
    const { permissions } = wizardContext;
    return permissions && permissions.dynamic && permissions.dynamic.EnableGoalLevelChange;
  }, [wizardContext]);

  const initiallySelectedNetwork = useMemo(() => {
    if (SelectedNetwork) {
      return SelectedNetwork;
    }

    if (goalHasAssociatedCampaigns) {
      return 'linkedin';
    }

    return 'microsoft';
  }, [SelectedNetwork, goalHasAssociatedCampaigns]);

  const acrossAllAccountsGoals = ['InStoreVisitGoal', 'ApplicationInstallGoal', 'InStoreTransactionGoal'];
  const isInStoreVisit = selectedGoalType === 'InStoreVisitGoal';
  const isAppInstallGoal = selectedGoalType === 'ApplicationInstallGoal';
  const lookBackWindowDefault = isInStoreVisit ? 1440 : 43200;
  const vtWindowDefault = 1440;
  const timeTemplate = isInStoreVisit ? dayTemplate : fullTimeTemplate;
  const timeErrorSummary = i18n.getString(_TL_('Expand to correct invalid input'));
  const [selectedScope, setSelectedScope] = useState(IsAccountLevel ? 'accountLevel' : 'customerLevel');
  const [selectedCount, setSelectedCount] = useState(ConversionCountType);
  // const [selectedNetwork, setSelectedNetwork] = useState(goalHasAssociatedCampaigns ? 'linkedin' : 'microsoft');
  const [selectedNetwork, setSelectedNetwork] = useState(initiallySelectedNetwork);
  const [isMainGoalSelectionChecked, setIsMainGoalSelectionChecked] = useState(IsMainConversionGoal);
  const [showUncheckConversionDialog, setShowUncheckConversionDialog] = useState(false);
  // conversion window and viewthrough window could be set to undefined as goal props updated
  const conversionWindowValue = _.isUndefined(LookbackWindowInMinutes) ? lookBackWindowDefault : LookbackWindowInMinutes;
  const initialCWObj = duration2Input(conversionWindowValue);
  const [conversionWindowSummary, setConversionWindowSummary] = useState(timeTemplate(initialCWObj, i18n));
  const [cwErrorStatus, setCwErrorStatus] = useState(LookbackWindowInMinutes === 0);
  const vtConverionWindowValue = _.isUndefined(ViewThroughLookbackWindowinMinutes) ? vtWindowDefault : ViewThroughLookbackWindowinMinutes;
  const initialVTCWObj = duration2Input(vtConverionWindowValue);
  const [vtConversionWindowSummary, setVTConversionWindowSummary] = useState(timeTemplate(initialVTCWObj, i18n));
  const [vtcwErrorStatus, setVtcwErrorStatus] = useState(ViewThroughLookbackWindowinMinutes === 0);
  const [attribute, setAttribute] = useState(AttributionModelType);
  const [expandStates, setExpandStates] = useState({
    scope: false,
    countType: false,
    conversionWindow: false,
    viewThroughWindow: false,
    mainGoal: false,
    linkedInAssociations: selectedNetwork === 'linkedin',
  });

  const LinkedInTooltip = useCallback(({ children }) => {
    if (selectedNetwork !== 'linkedin') { return children; }

    const tooltipContent = i18n.getString(_TL_('Select Microsoft Advertising Network if you want to set up a conversion goal as you typically would.'));

    return (
      <Tooltip
        relationship="description"
        content={tooltipContent}
      >
        <div style={{ display: 'inline-block' }}>
          {children}
        </div>
      </Tooltip>
    );
  }, [i18n, selectedNetwork]);

  const renderWithLinkedInTooltip = useCallback((fieldProps, render) => (<LinkedInTooltip>{render(fieldProps)}</LinkedInTooltip>), [LinkedInTooltip]);

  const scopeOptions = useMemo(() => {
    let disableAccountLevel = acrossAllAccountsGoals.indexOf(wizardContext.goalContext.Type) > -1;
    let disableCustomerLevel = false;
    if (isEditScenario) {
      disableAccountLevel = !enableGoalLevelChange || !IsAccountLevel;
      disableCustomerLevel = !enableGoalLevelChange;
    }

    disableCustomerLevel = disableCustomerLevel || selectedNetwork === 'linkedin';

    const onAccountLabel = `${i18n.getString(_TL_('On account:'))} ${accountName}`;
    const acrossAccountsLabel = i18n.getString(_TL_('Across all accounts'));

    return [
      {
        key: 'accountLevel', ariaLabel: onAccountLabel, text: <span tabIndex="0" role="button">{onAccountLabel} </span>, disabled: disableAccountLevel,
      },
      {
        key: 'customerLevel', ariaLabel: acrossAccountsLabel, text: <span tabIndex="0" role="button"> {acrossAccountsLabel} </span>, disabled: disableCustomerLevel, onRenderField: renderWithLinkedInTooltip,
      },
    ];
  }, [IsAccountLevel, accountName, acrossAllAccountsGoals, enableGoalLevelChange, i18n, isEditScenario, wizardContext.goalContext.Type, selectedNetwork, renderWithLinkedInTooltip]);

  const countOptions = useMemo(() => {
    const disableAllOption = isInStoreVisit;
    const disableUniqueOption = isAppInstallGoal || selectedNetwork === 'linkedin';

    const allCountLabel = i18n.getString(_TL_('All (for example, if one ad click leads to three purchases, that will count as three conversions.)'));
    const uniqueCountLabel = i18n.getString(_TL_('Unique (for example, if one ad click leads to three purchases, that will count as one conversion.)'));

    return [
      {
        key: 'All', ariaLabel: allCountLabel, text: <span tabIndex="0" role="button"> {allCountLabel} </span>, disabled: disableAllOption,
      },
      {
        key: 'Unique', ariaLabel: uniqueCountLabel, text: <span tabIndex="0" role="button"> {uniqueCountLabel} </span>, disabled: disableUniqueOption, onRenderField: renderWithLinkedInTooltip,
      },
    ];
  }, [i18n, isInStoreVisit, isAppInstallGoal, selectedNetwork, renderWithLinkedInTooltip]);

  const disableIncludeInConversions = isInStoreVisit || selectedNetwork === 'linkedin';

  const disableAttributionModel = selectedNetwork === 'linkedin';

  useEffect(() => {
    if (expandError) {
      setExpandStates({
        ...expandStates,
        conversionWindow: cwErrorStatus ? true : expandStates.conversionWindow,
        viewThroughWindow: vtcwErrorStatus ? true : expandStates.viewThroughWindow,
      });
      resetExpandError();
    }
  }, [IsViewThroughConversionWindowVisible, cwErrorStatus, expandError, expandStates, resetExpandError, updateGoalContext, vtcwErrorStatus]);

  useEffect(() => {
    setAdvSettingHasError(cwErrorStatus || vtcwErrorStatus);
  }, [cwErrorStatus, setAdvSettingHasError, vtcwErrorStatus]);

  const toggle = useCallback((event) => {
    const key = event.target.dataset.stateId;

    if (!expandStates[key]) {
      logActivity.trace(`AdvancedGoalSettings:RegularGoal:Expanded toggle for ${key}`);
    }
    setExpandStates({
      ...expandStates,
      [key]: !expandStates[key],
    });
  }, [expandStates, logActivity]);

  const onUpdateLookBackWindow = useCallback((updatedTime, error) => {
    updateGoalContext({ LookbackWindowInMinutes: error ? NaN : updatedTime });
    if (error) {
      setCwErrorStatus(true);
    } else {
      setCwErrorStatus(false);
      setConversionWindowSummary(timeTemplate(duration2Input(updatedTime), i18n));
    }
  }, [updateGoalContext, timeTemplate, i18n]);

  const onUpdateViewThroughWindow = useCallback((updatedTime, error) => {
    updateGoalContext({ ViewThroughLookbackWindowinMinutes: error ? NaN : updatedTime });
    if (error) {
      setVtcwErrorStatus(true);
    } else {
      setVtcwErrorStatus(false);
      setVTConversionWindowSummary(timeTemplate(duration2Input(updatedTime), i18n));
    }
  }, [updateGoalContext, timeTemplate, i18n]);

  const onChangeScope = useCallback((event, option) => {
    setSelectedScope(option.key);
    updateGoalContext({ IsAccountLevel: option.key === 'accountLevel' });
  }, [updateGoalContext]);

  const onChangeCount = useCallback((event, option) => {
    setSelectedCount(option.key);
    updateGoalContext({ ConversionCountType: option.key });
  }, [updateGoalContext]);

  const onChangeMainGoalSelection = useCallback((event, value) => {
    if (value) {
      setIsMainGoalSelectionChecked(value);
      updateGoalContext({ IsMainConversionGoal: value });
    } else {
      setShowUncheckConversionDialog(true);
    }
  }, [updateGoalContext]);

  const onChangeAttributeModel = useCallback((event, option) => {
    setAttribute(option.key);
    updateGoalContext({ AttributionModelType: option.key });
  }, [updateGoalContext]);

  const thirtyDayString = i18n.getString(_TL_('Enter a conversion period of up to 30 days.'));
  const ninetyDayString = i18n.getString(_TL_('Enter a conversion period of up to 90 days.'));

  const uncheckDialogConfirm = useCallback(() => {
    setIsMainGoalSelectionChecked(false);
    setShowUncheckConversionDialog(false);
    updateGoalContext({ IsMainConversionGoal: false });
  }, [updateGoalContext]);

  const uncheckDialogCancel = useCallback(() => {
    setIsMainGoalSelectionChecked(true);
    setShowUncheckConversionDialog(false);
    updateGoalContext({ IsMainConversionGoal: true });
  }, [updateGoalContext]);

  const [valuesBeforeNetworkChange, setValuesBeforeNetworkChange] = useState({});

  const onChangeSelectedNetwork = useCallback((network) => {
    // Reset conversion window to default value
    onUpdateLookBackWindow(43200);

    if (network === selectedNetwork) { return; }

    if (network === 'linkedin') {
      setExpandStates({ ...expandStates, linkedInAssociations: true });

      setValuesBeforeNetworkChange({
        selectedScope,
        selectedCount,
        isMainGoalSelectionChecked,
        attribute,
      });

      setSelectedScope('accountLevel');
      setIsMainGoalSelectionChecked(true);
      setSelectedCount('All');
      setAttribute('Default');


      updateGoalContext({
        IsAccountLevel: true,
        IsMainConversionGoal: true,
        ConversionCountType: 'All',
        AttributionModelType: 'Default',
        LookbackWindowInMinutes: 43200,
        SelectedNetwork: 'linkedin',
      });
    } else if (!_.isEmpty(valuesBeforeNetworkChange)) {
      setSelectedScope(valuesBeforeNetworkChange.selectedScope);
      setIsMainGoalSelectionChecked(valuesBeforeNetworkChange.isMainGoalSelectionChecked);
      setSelectedCount(valuesBeforeNetworkChange.selectedCount);
      setAttribute(valuesBeforeNetworkChange.attribute);

      updateGoalContext({
        IsAccountLevel: valuesBeforeNetworkChange.selectedScope === 'accountLevel',
        IsMainConversionGoal: valuesBeforeNetworkChange.isMainGoalSelectionChecked,
        ConversionCountType: valuesBeforeNetworkChange.selectedCount,
        AttributionModelType: valuesBeforeNetworkChange.attribute,
        LookbackWindowInMinutes: 43200,
        SelectedNetwork: 'microsoft',
      });
    }

    setSelectedNetwork(network);
  }, [
    selectedNetwork, valuesBeforeNetworkChange, selectedScope, selectedCount, isMainGoalSelectionChecked, attribute, expandStates, setSelectedNetwork,
    setSelectedScope, setIsMainGoalSelectionChecked, setSelectedCount, setAttribute, setValuesBeforeNetworkChange, updateGoalContext,
    onUpdateLookBackWindow,
  ]);

  const isMaxWindowLimit30days = isInStoreVisit || selectedNetwork === 'linkedin';

  return (
    <div className={classes.section}>
      <div className={classes.defaultTitle} role="heading" aria-level={2}>{i18n.getString(_TL_('Advanced settings'))}</div>
      {/* networks */}
      {showLinkedInCards && (
        <NetworkCards
          scenario={isEditScenario ? 'edit' : 'create'}
          selected={selectedNetwork}
          disabled={goalHasAssociatedCampaigns}
          onChange={onChangeSelectedNetwork}
        />
      )}
      {/* scope */}
      <div className={classnames(classes.accordionBlock, 'scope-block')}>
        <AccordionHeader
          data-state-id="scope"
          expanded={expandStates.scope}
          onChange={toggle}
          helpId="pop_BA_UET_Scope"
          titleId="scope-label"
          i18n={i18n}
        >
          {i18n.getString(_TL_('Scope'))}
        </AccordionHeader>
        <div>
          {expandStates.scope ?
          (
            <ChoiceGroup
              selectedKey={selectedScope}
              options={scopeOptions}
              onChange={onChangeScope}
              aria-labelledby="scope-label"
            />
          ) :
          (
            <p>{mappingChoiceText(scopeOptions, selectedScope)}</p>
          )}
        </div>
      </div>
      {/* count */}
      <div className={classnames(classes.accordionBlock, 'count-type-block')}>
        <AccordionHeader
          data-state-id="countType"
          expanded={expandStates.countType}
          onChange={toggle}
          helpId="pop_BA_UET_Count"
          i18n={i18n}
        >
          {i18n.getString(_TL_('Count'))}
        </AccordionHeader>
        <div className={expandStates.countType ? 'displayBlock' : 'hideBlock'}>
          <p className={classes.bodyText}>
            <span id="count-type-label">{i18n.getString(_TL_('Select how you\'d like to count your conversions.'))}</span>
            <span> </span>
            <LearnMore i18n={i18n} topicId="app51214" ariaLabel={i18n.getString(_TL_('Learn more'))} />
          </p>
          <ChoiceGroup
            selectedKey={selectedCount}
            options={countOptions}
            onChange={onChangeCount}
            aria-labelledby="count-type-label"
          />
        </div>
        <div className={expandStates.countType ? 'hideBlock' : 'displayBlock'}>
          <p>{mappingChoiceText(countOptions, selectedCount)}</p>
        </div>
      </div>
      {/* conversion window */}
      <div className={classnames(classes.accordionBlock, 'conversion-window-block')}>
        <AccordionHeader
          data-state-id="conversionWindow"
          expanded={expandStates.conversionWindow}
          onChange={toggle}
          helpId="pop_BA_UET_ConversionWindow"
          i18n={i18n}
        >
          {i18n.getString(_TL_('Conversion window'))}
        </AccordionHeader>
        <div>
          <div className={expandStates.conversionWindow ? 'displayBlock' : 'hideBlock'}>
            <p id="conversion-window-label" className={classes.bodyText}>{isMaxWindowLimit30days ? thirtyDayString : ninetyDayString}</p>
            <ConversionWindow
              key={isMaxWindowLimit30days}
              updateWindowProp={onUpdateLookBackWindow}
              conversionWindowInMin={LookbackWindowInMinutes}
              defaultValue={lookBackWindowDefault}
              maxWindowInMin={isMaxWindowLimit30days ? 43200 : 129600}
              minWindowInMin={isInStoreVisit ? 1440 : 0}
              hideHourMinute={isInStoreVisit}
              label="conversion-window-label"
              logActivity={logActivity}
            />
          </div>
          <div className={expandStates.conversionWindow ? 'hideBlock' : 'displayBlock'}>
            <p>{cwErrorStatus ? timeErrorSummary : conversionWindowSummary}</p>
          </div>
        </div>
      </div>
      {/* view-through conversion window (pilot) */}
      {IsViewThroughConversionWindowVisible &&
      (
      <div className={classnames(classes.accordionBlock, 'viewthrough-window-block')}>
        <AccordionHeader
          data-state-id="viewThroughWindow"
          expanded={expandStates.viewThroughWindow}
          onChange={toggle}
          helpId="pop_BA_UET_VTCWindow"
          i18n={i18n}
        >
          {i18n.getString(_TL_('View-through Conversion window'))}
        </AccordionHeader>
        <div>
          <div className={expandStates.viewThroughWindow ? 'displayBlock' : 'hideBlock'}>
            <p id="view-through-window-label" className={classes.bodyText}>{thirtyDayString}</p>
            <ConversionWindow
              updateWindowProp={onUpdateViewThroughWindow}
              conversionWindowInMin={ViewThroughLookbackWindowinMinutes}
              defaultValue={vtWindowDefault}
              maxWindowInMin={43200}
              label="view-through-window-label"
            />
          </div>
          <div className={expandStates.viewThroughWindow ? 'hideBlock' : 'displayBlock'}>
            <p>{vtcwErrorStatus ? timeErrorSummary : vtConversionWindowSummary}</p>
          </div>
        </div>
      </div>)}
      {/* main goal selection */}
      <div className={classnames(classes.accordionBlock, 'maingoal-block')}>
        <AccordionHeader
          data-state-id="mainGoal"
          expanded={expandStates.mainGoal}
          onChange={toggle}
          helpId="pop_BA_ConversionGoal_CountAsConv"
          i18n={i18n}
        >
          {i18n.getString(_TL_('Include in "Conversions"'))}
        </AccordionHeader>
        <div>
          {expandStates.mainGoal ?
          (
            <div>
              <LinkedInTooltip>
                <Checkbox
                  label={i18n.getString(_TL_('These conversions will appear in the "Conversions" column in the data table and will also affect any automated bidding bid strategies you have enabled.'))}
                  checked={isMainGoalSelectionChecked}
                  onChange={onChangeMainGoalSelection}
                  disabled={disableIncludeInConversions}
                />
              </LinkedInTooltip>
              <UncheckConversionDialog
                checkIncludeCallback={uncheckDialogConfirm}
                uncheckIncludeCallback={uncheckDialogCancel}
                shouldShowDialog={showUncheckConversionDialog && !isInStoreVisit}
              />
            </div>
          ) :
          (
            <p>{isMainGoalSelectionChecked ? i18n.getString(_TL_('Yes')) : i18n.getString(_TL_('No'))}</p>
          )}
        </div>
      </div>
      {
        isConversionGoalAttributionModelEnabled &&
        <div className={classnames(classes.accordionBlock, 'attribuete-model-block')}>
          <AccordionHeader
            data-state-id="attributeModel"
            expanded={expandStates.attributeModel}
            onChange={toggle}
            helpId="pop_BA_CampaignGrids_AttributionModelType"
            i18n={i18n}
          >
            {i18n.getString(_TL_('Conversion attribution model'))}
          </AccordionHeader>
          <div>
            {expandStates.attributeModel ?
              <LinkedInTooltip>
                <Dropdown
                  className={classes.attributeModelDropdown}
                  selectedKey={attribute}
                  onChange={onChangeAttributeModel}
                  ariaLabel={i18n.getString(_TL_('Select attribution model'))}
                  options={_.compact([
                    !IsAmplifyEnabled && { key: 'Default', text: i18n.getString(_TL_('Last Click')) },
                    { key: 'LastTouch', text: i18n.getString(_TL_('Last Touch')) },
                  ])}
                  dropdownWidth="auto"
                  disabled={disableAttributionModel}
                />
              </LinkedInTooltip> :
              <p className="attribute-model">{attribute === 'Default' ? i18n.getString(_TL_('Last Click')) : i18n.getString(_TL_('Last Touch'))}</p>
            }
          </div>
        </div>
      }
      {
        selectedNetwork === 'linkedin' && (
          <div className={classnames(classes.accordionBlock, 'linkedin-campaign-associations-block')}>
            <AccordionHeader
              data-state-id="linkedInAssociations"
              expanded={expandStates.linkedInAssociations}
              onChange={toggle}
              helpId="pop_BA_LinkedIn_AdvancedSettings" // TODO: add help topic
              i18n={i18n}
            >
              {i18n.getString(_TL_('Selected LinkedIn campaigns'))}
            </AccordionHeader>
            <div>
              {expandStates.linkedInAssociations && (
                <LinkedInCampaignAssociationSelector
                  cid={cid}
                  aid={aid}
                  associatedCampaigns={associatedCampaigns}
                  onAssociatedCampaignsChange={onAssociatedCampaignsChange}
                />
              )}
            </div>
          </div>
        )
      }
    </div>
  );
};

AdvancedGoalSettingsViewUnstyled.propTypes = {
  classes: PropTypes.shape(_.mapObject(
    getStepStyles(),
    _.constant(PropTypes.string.isRequired)
  )).isRequired,
  updateGoalContext: PropTypes.func.isRequired,
  selectedGoalType: PropTypes.string.isRequired,
  expandError: PropTypes.bool.isRequired,
  isConversionGoalAttributionModelEnabled: PropTypes.bool,
  resetExpandError: PropTypes.func.isRequired,
  setAdvSettingHasError: PropTypes.func,
  showLinkedInCards: PropTypes.bool.isRequired,
  goalHasAssociatedCampaigns: PropTypes.bool.isRequired,
  associatedCampaigns: PropTypes.arrayOf(PropTypes.shape({
  })).isRequired,
  onAssociatedCampaignsChange: PropTypes.func.isRequired,
};

AdvancedGoalSettingsViewUnstyled.defaultProps = {
  isConversionGoalAttributionModelEnabled: false,
  setAdvSettingHasError: _.noop,
};

export const AdvancedGoalSettingsView = withDefaultStyles(AdvancedGoalSettingsViewUnstyled, getStepStyles);
